import { useEffect, useRef, useState, useCallback } from "react";
import * as Y from "yjs";
import { WebsocketProvider } from "y-websocket";
import { useAuthStore } from "../../stores/auth";
import { api } from "../../lib/api";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";
const WS_BASE = API_URL.replace(/^http/, "ws");
const ROOM_PREFIX = "api/v1/ws/collab";

export interface CollaboratorPresence {
  clientId: number;
  user: {
    name: string;
    color: string;
    avatar?: string;
  };
  cursor?: {
    line: number;
    column: number;
  };
  selection?: {
    anchor: number;
    head: number;
  };
  lastActive: number;
}

export interface UseCollaborationOptions {
  documentId: string | null;
  userName?: string;
  userColor?: string;
  userAvatar?: string;
}

export interface UseCollaborationReturn {
  doc: Y.Doc | null;
  provider: WebsocketProvider | null;
  awareness: import("y-protocols/awareness").Awareness | null;
  isConnected: boolean;
  isSynced: boolean;
  collaborators: CollaboratorPresence[];
  updateCursor: (line: number, column: number) => void;
  updateSelection: (anchor: number, head: number) => void;
}

const COLLABORATOR_COLORS = [
  "#5B5FFF", // electric-indigo
  "#7C3AED", // lever-violet
  "#A78BFA", // soft-iris
  "#3B82F6", // cool-azure
  "#34D399", // signal-green
  "#FB923C", // ember-orange
  "#F87171", // soft-red
];

function pickColor(clientId: number): string {
  return COLLABORATOR_COLORS[Math.abs(clientId) % COLLABORATOR_COLORS.length];
}

export function useCollaboration({
  documentId,
  userName = "Anonymous",
  userColor,
  userAvatar,
}: UseCollaborationOptions): UseCollaborationReturn {
  const [doc, setDoc] = useState<Y.Doc | null>(null);
  const [provider, setProvider] = useState<WebsocketProvider | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isSynced, setIsSynced] = useState(false);
  const [collaborators, setCollaborators] = useState<CollaboratorPresence[]>([]);
  const providerRef = useRef<WebsocketProvider | null>(null);
  const docRef = useRef<Y.Doc | null>(null);

  const cleanup = useCallback(() => {
    const p = providerRef.current;
    if (p) {
      p.destroy();
      providerRef.current = null;
    }
    const d = docRef.current;
    if (d) {
      d.destroy();
      docRef.current = null;
    }
    setProvider(null);
    setDoc(null);
    setIsConnected(false);
    setIsSynced(false);
    setCollaborators([]);
  }, []);

  const updateCursor = useCallback((line: number, column: number) => {
    const p = providerRef.current;
    if (!p) return;
    const current = p.awareness.getLocalState() || {};
    p.awareness.setLocalState({
      ...current,
      cursor: { line, column },
      lastActive: Date.now(),
    });
  }, []);

  const updateSelection = useCallback((anchor: number, head: number) => {
    const p = providerRef.current;
    if (!p) return;
    const current = p.awareness.getLocalState() || {};
    p.awareness.setLocalState({
      ...current,
      selection: { anchor, head },
      lastActive: Date.now(),
    });
  }, []);

  // Connection lifecycle — only tears down when the document changes.
  useEffect(() => {
    if (!documentId) {
      cleanup();
      return;
    }

    const token = useAuthStore.getState().session?.access_token || api.getToken() || "";
    if (!token) {
      return;
    }

    const ydoc = new Y.Doc();
    const roomName = `${ROOM_PREFIX}/${documentId}`;
    const wsProvider = new WebsocketProvider(WS_BASE, roomName, ydoc, {
      connect: true,
      params: { token },
    });

    providerRef.current = wsProvider;
    docRef.current = ydoc;

    const handleStatus = (event: { status: "connected" | "disconnected" | "connecting" }) => {
      setIsConnected(event.status === "connected");
    };

    const handleSync = (synced: boolean) => {
      setIsSynced(synced);
    };

    const handleAwarenessChange = () => {
      const states = wsProvider.awareness.getStates();
      const localClientId = ydoc.clientID;
      const peers: CollaboratorPresence[] = [];

      states.forEach((state, clientId) => {
        if (clientId === localClientId) return;
        if (!state?.user) return;
        peers.push({
          clientId,
          user: state.user,
          cursor: state.cursor,
          selection: state.selection,
          lastActive: state.lastActive ?? Date.now(),
        });
      });

      setCollaborators(peers);
    };

    wsProvider.on("status", handleStatus);
    wsProvider.on("sync", handleSync);
    wsProvider.awareness.on("change", handleAwarenessChange);

    setDoc(ydoc);
    setProvider(wsProvider);
    setIsConnected(wsProvider.wsconnected);
    setIsSynced(wsProvider.synced);

    return () => {
      wsProvider.off("status", handleStatus);
      wsProvider.off("sync", handleSync);
      wsProvider.awareness.off("change", handleAwarenessChange);
      cleanup();
    };
  }, [documentId, cleanup]);

  // Awareness presence — updates in-place without reconnecting.
  useEffect(() => {
    const p = providerRef.current;
    if (!p) return;
    const color = userColor ?? pickColor(p.doc.clientID);
    const current = p.awareness.getLocalState() || {};
    p.awareness.setLocalState({
      ...current,
      user: { name: userName, color, avatar: userAvatar },
      lastActive: Date.now(),
    });
  }, [userName, userColor, userAvatar]);

  return {
    doc,
    provider,
    awareness: provider?.awareness ?? null,
    isConnected,
    isSynced,
    collaborators,
    updateCursor,
    updateSelection,
  };
}
